import { useState, useEffect } from "react";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface ScoreItem {
  label: string;
  key: string;
  weight: number;
  done: boolean;
  hint: string;
}

interface ProviderProfile {
  service_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  cover_image_url: string | null;
  slogan: string | null;
  location: string | null;
  philosophy: string | null;
  skills: string[] | null;
  faq: unknown[] | null;
}

export default function ProviderProfileScore() {
  const { currentUser } = useApp();
  const [profile, setProfile] = useState<ProviderProfile | null>(null);
  const [productCount, setProductCount] = useState(0);
  const [animScore, setAnimScore] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.id) return;
    const load = async () => {
      const [{ data: prof }, { count }] = await Promise.all([
        supabase
          .from("profiles")
          .select("service_name, bio, avatar_url, cover_image_url, slogan, location, philosophy, skills, faq")
          .eq("id", currentUser.id)
          .maybeSingle(),
        supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .eq("provider_id", currentUser.id)
          .eq("visibility", "public"),
      ]);
      setProfile(prof || null);
      setProductCount(count || 0);
      setLoading(false);
    };
    load();
  }, [currentUser?.id]);

  const items: ScoreItem[] = profile ? [
    { label: "ショップ名", key: "service_name", weight: 10, done: !!profile.service_name, hint: "「プロフィール設定」からショップ名を追加しましょう" },
    { label: "自己紹介文", key: "bio", weight: 15, done: !!(profile.bio && profile.bio.length > 30), hint: "30文字以上の自己紹介文を書きましょう" },
    { label: "プロフィール画像", key: "avatar_url", weight: 15, done: !!profile.avatar_url, hint: "プロフィール写真をアップロードしましょう" },
    { label: "カバー画像", key: "cover_image_url", weight: 10, done: !!profile.cover_image_url, hint: "ショップのカバー画像を設定しましょう" },
    { label: "キャッチコピー", key: "slogan", weight: 10, done: !!profile.slogan, hint: "あなたのサービスを一言で表すキャッチコピーを追加しましょう" },
    { label: "所在地", key: "location", weight: 5, done: !!profile.location, hint: "活動地域や所在地を入力しましょう" },
    { label: "理念・哲学", key: "philosophy", weight: 15, done: !!(profile.philosophy && profile.philosophy.length > 50), hint: "事業への想いや哲学を50文字以上で記述しましょう" },
    { label: "スキルタグ", key: "skills", weight: 10, done: !!(profile.skills && profile.skills.length >= 3), hint: "3つ以上のスキルタグを追加しましょう" },
    { label: "FAQ設定", key: "faq", weight: 5, done: !!(profile.faq && (profile.faq as unknown[]).length >= 2), hint: "よくある質問を2つ以上設定しましょう" },
    { label: "商品を出品中", key: "product", weight: 5, done: productCount > 0, hint: "少なくとも1つ商品を公開しましょう" },
  ] : [];

  const score = items.reduce((s, item) => s + (item.done ? item.weight : 0), 0);
  const doneCount = items.filter((i) => i.done).length;
  const totalItems = items.length;

  useEffect(() => {
    if (loading) return;
    const step = score / 50;
    let current = 0;
    const interval = setInterval(() => {
      current = Math.min(current + step, score);
      setAnimScore(Math.round(current));
      if (current >= score) clearInterval(interval);
    }, 20);
    return () => clearInterval(interval);
  }, [score, loading]);

  const getColor = (s: number) => {
    if (s >= 80) return "#2D6A4F";
    if (s >= 50) return "#C9A84C";
    return "#D4A017";
  };
  const getLabel = (s: number) => {
    if (s >= 90) return "🏆 完璧！素晴らしいプロフィールです";
    if (s >= 70) return "✨ 充実しています！あと少し！";
    if (s >= 50) return "📈 まずまずです。さらに充実させましょう";
    return "🚀 プロフィールを充実させて集客アップ！";
  };

  if (loading) return <div className="flex items-center justify-center py-10"><i className="ri-loader-4-line animate-spin text-xl text-teal" /></div>;

  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (animScore / 100) * circumference;

  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
      <div className="px-6 py-5 border-b border-stone-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-display italic text-gold text-xs tracking-widest mb-1">Profile Score</p>
            <h3 className="font-serif text-deep-green text-xl font-light">プロフィール完成度</h3>
          </div>
          <div className="flex items-center gap-4">
            {/* Animated circular gauge */}
            <div className="relative w-28 h-28 flex-shrink-0">
              <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="none" stroke="#f1f0ec" strokeWidth="10" />
                <circle
                  cx="60" cy="60" r="54"
                  fill="none"
                  stroke={getColor(animScore)}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  style={{ transition: "stroke-dashoffset 0.05s linear, stroke 0.3s ease" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="font-display text-2xl font-light" style={{ color: getColor(animScore) }}>{animScore}</p>
                <p className="text-xs text-charcoal/40 font-sans">/ 100</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-sans font-medium text-charcoal mb-1">{getLabel(score)}</p>
              <p className="text-xs text-charcoal/50 font-sans">{doneCount}/{totalItems} 項目完了</p>
              <div className="mt-2 h-1.5 w-32 bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${(doneCount / totalItems) * 100}%`, backgroundColor: getColor(score) }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="p-6">
        <p className="text-xs text-charcoal/40 font-sans mb-4">改善ポイント一覧</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.map((item) => (
            <div
              key={item.key}
              className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${item.done ? "border-teal/20 bg-teal/3" : "border-stone-100 bg-stone-50"}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.done ? "bg-teal" : "bg-stone-200"}`}>
                {item.done ? (
                  <i className="ri-check-line text-white" style={{ fontSize: "11px" }} />
                ) : (
                  <span className="text-charcoal/30 text-xs font-bold">!</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-xs font-sans font-medium ${item.done ? "text-teal" : "text-charcoal"}`}>{item.label}</p>
                  <span className="text-xs text-charcoal/30 font-sans flex-shrink-0">+{item.weight}pt</span>
                </div>
                {!item.done && <p className="text-xs text-charcoal/40 font-sans mt-0.5 leading-snug">{item.hint}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
